import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DialogRef, KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import { TranslationPipe } from '@raptor-fe/services';
import { WebSelectBranchModalStore } from './web-select-branch-modal.store';

@Component({
    selector: 'lib-web-select-branch-modal',
    template: `
        <kendo-dialog
            minWidth="80vw"
            maxHeight="90vh"
            (close)="onClose()"
            autoFocusedElement="#branch-search-input"
        >
            <kendo-dialog-titlebar>
                <h6>{{ { feature: "Web App", component: "Login", label: "Change Branch" } | translate }}</h6>
            </kendo-dialog-titlebar>

            <div class="px-3 border rounded-md bg-body">
                <input
                    id="branch-search-input"
                    class="input-sm my-2 form-control"
                    placeholder="{{ { feature: 'Web App', component: 'Login', label: 'Search Branches' } | translate }}"
                    (keyup)="search($event); branchList.scrollTop = 0"
                />
                @if(store.selectedBranch() && store.selectedBranch()?.name !== ''){
                <div class="row border-bottom py-2">
                    <div class="col-md-6">
                        <span>{{ { feature: "Web App", component: "Login", label: "Current Branch" } | translate }}</span>
                    </div>
                    <div class="col-md-6">
                        <h6>{{ store.selectedBranch()?.name }}</h6>
                    </div>
                </div>
                }
                <ul (scroll)="onScroll($event)" class="overflow-y-auto max-h-[55vh]" #branchList>
                    @for (item of store.branches(); track item) {
                    <li class="row border-bottom py-2" (click)="store.setSelectedBranch(item)" aria-hidden="true">
                        <div class="col-md-10">
                            <div class="row">
                                <div class="col-md-12">
                                    {{ item.name }}
                                </div>
                                @if(item.contact){
                                <div class="col-md-4">
                                    <p class="form-text">
                                        <b>{{ { feature: "Web App", component: "Login", label: "Contact" } | translate }}:</b>
                                        {{ item.contact.name }}
                                    </p>
                                </div>
                                <div class="col-md-4">
                                    <p class="form-text">
                                        <b>{{ { feature: "Web App", component: "Login", label: "Email" } | translate }}:</b>
                                        {{ item.contact.email }}
                                    </p>
                                </div>
                                <div class="col-md-4">
                                    <p class="form-text">
                                        <b>{{ { feature: "Web App", component: "Login", label: "Phone" } | translate }}:</b>
                                        {{ item.contact.phoneNumber }}
                                    </p>
                                </div>
                                @if(item.contact.address) {
                                <div class="col-md-12">
                                    <p class="form-text">
                                        <b>{{ { feature: "Web App", component: "Login", label: "Address" } | translate }}:</b>
                                        {{ item.contact.address.line1 }} {{ item.contact.address.line2 }}
                                        {{ item.contact.address.city }} {{ item.contact.address.state }}
                                        {{ item.contact.address.zip }}
                                    </p>
                                </div>
                                } }
                            </div>
                        </div>
                        <div class="col-md-2">
                            <input type="radio" name="branch" [checked]="item.id === store.selectedBranch()?.id" (change)="store.setSelectedBranch(item)" />
                        </div>
                    </li>
                    }
                </ul>
            </div>

             <kendo-dialog-actions layout="end">
                <button
                    class="btn btn-sm btn-secondary min-w-24"
                    (click)="onClose()"
                    [disabled]="store.selectedBranch() === null"
                >
                    {{ { feature: "Common", component: "Button", label: "Cancel" } | translate }}
                </button>
                <button class="btn btn-sm btn-primary min-w-24" (click)="onClose()" [disabled]="store.selectedBranch() === null">
                    {{ { feature: "Common", component: "Button", label: "Confirm" } | translate }}
                </button>
            </kendo-dialog-actions>

        </kendo-dialog>

    `,
    styles: [
        `
            ion-content {
                --padding-start: 3%;
                --padding-end: 3%;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslationPipe, KENDO_DIALOGS],
})
export class WebSelectBranchModalComponent {
    protected readonly store = inject(WebSelectBranchModalStore);
    // keep a reference to the dialog
    protected readonly dialogRef = inject(DialogRef);

    search(event: KeyboardEvent): void {
        const searchText = event.target as HTMLInputElement;
        this.store.updateSearchTerm(searchText.value ?? '');
    }

    protected async onClose(): Promise<void> {
        this.store.close(this.dialogRef);
    }

    protected onScroll(event: Event): void {
        if (!this.atBottom(event) || this.store.isScrollDisabled()) {
            return;
        }

        this.store.increasePageNumber();
    }

    private atBottom(event: Event) {
        const tracker = event.target as HTMLElement;
        const limit = tracker.scrollHeight - tracker.clientHeight;
        return tracker.scrollTop === limit;
    }
}
